import { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  Typography,
} from "@mui/material";
import { useSelector } from "react-redux";
import {
  useGetApiTokensQuery,
  useGetAccountsQuery,
  useDisableApiTokenMutation,
  useEnableApiTokenMutation,
  useDeleteApiTokenMutation,
} from "../../../redux/api";
import APITokenTable from "./APITokenTable";
import APITokenFormDrawer from "./APITokenFormDrawer";
import APITokenHistoryDrawer from "./APITokenHistoryDrawer";

export default function APIManagementPage() {
  const { account } = useSelector((state) => state.queryParams);
  const isAllMode = account?.toLowerCase() === "all";

  const [accountFilter, setAccountFilter] = useState("ALL");
  const [drawerTarget, setDrawerTarget] = useState(null);
  const [historyTarget, setHistoryTarget] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [toast, setToast] = useState({ open: false, message: "", severity: "success" });

  // ── API calls ──────────────────────────────────────────────────────────────

  const tokensQuery = useGetApiTokensQuery(
    { account },
    { skip: !account }
  );

  const accountsQuery = useGetAccountsQuery(undefined, { skip: !isAllMode });

  const [disableApiToken] = useDisableApiTokenMutation();
  const [enableApiToken] = useEnableApiTokenMutation();
  const [deleteApiToken] = useDeleteApiTokenMutation();

  // ── Derived data ───────────────────────────────────────────────────────────

  const allTokens = useMemo(() => {
    const ok =
      tokensQuery.data?.current_state === "SUCCESS" ||
      tokensQuery.data?.status === "SUCCESS";
    return ok ? (tokensQuery.data?.data || []) : [];
  }, [tokensQuery.data]);

  const accounts = useMemo(() => {
    const payload = accountsQuery.data;
    const ok = payload?.current_state === "SUCCESS" || payload?.status === "SUCCESS";
    if (!ok) return [];
    const raw = payload?.data?.accounts || payload?.accounts || [];
    return ["ALL", ...Array.from(new Set(Array.isArray(raw) ? raw : [])).sort((a, b) => a.localeCompare(b))];
  }, [accountsQuery.data]);

  const visibleTokens = useMemo(() => {
    if (!isAllMode || accountFilter === "ALL") return allTokens;
    return allTokens.filter((t) => t.account === accountFilter);
  }, [allTokens, isAllMode, accountFilter]);

  // ── Helpers ────────────────────────────────────────────────────────────────

  const showToast = (message, severity = "success") =>
    setToast({ open: true, message, severity });

  const refetch = () => tokensQuery.refetch();

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleDisable = async (token) => {
    const result = await disableApiToken({ tokenid: token.id, tokenname: token.name });
    if (result.data?.current_state === "SUCCESS") {
      showToast(`Token "${token.name}" disabled.`);
      refetch();
    } else {
      showToast(result.data?.message || "Failed to disable token.", "error");
    }
  };

  const handleEnable = async (token) => {
    const result = await enableApiToken({ tokenid: token.id, tokenname: token.name });
    if (result.data?.current_state === "SUCCESS") {
      showToast(`Token "${token.name}" enabled.`);
      refetch();
    } else {
      showToast(result.data?.message || "Failed to enable token.", "error");
    }
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    const result = await deleteApiToken({ tokenid: pendingDelete.id, tokenname: pendingDelete.name });
    setPendingDelete(null);
    if (result.data?.current_state === "SUCCESS") {
      showToast(`Token "${pendingDelete.name}" deleted.`);
      refetch();
    } else {
      showToast(result.data?.message || "Failed to delete token.", "error");
    }
  };

  // ── Error / loading states ─────────────────────────────────────────────────

  const apiError =
    tokensQuery.error?.data?.message ||
    tokensQuery.error?.error ||
    (allTokens.length === 0 && tokensQuery.data && tokensQuery.data?.current_state !== "SUCCESS" && tokensQuery.data?.status !== "SUCCESS"
      ? tokensQuery.data?.message
      : null);

  const isLoading = tokensQuery.isLoading || tokensQuery.isFetching;

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ p: 2 }}>
      {/* Page header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h5">API Token Management</Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="contained" onClick={() => setDrawerTarget({ isNew: true, tokenType: "STATIC" })}>
            + Add
          </Button>
          <Button variant="outlined" onClick={() => setDrawerTarget({ isNew: true, tokenType: "DYNAMIC" })}>
            + Add Dynamic Token
          </Button>
        </Stack>
      </Stack>

      {!account && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Account is missing in URL/query params.
        </Alert>
      )}

      {apiError && (
        <Alert severity="error" sx={{ mb: 2 }}>{apiError}</Alert>
      )}

      {/* Account filter — Mode A (ALL) only */}
      {isAllMode && (
        <Box
          sx={{
            mb: 2,
            border: "1px solid #d5d9e0",
            borderRadius: 2,
            bgcolor: "#f8f9fc",
            overflow: "hidden",
          }}
        >
          <Box sx={{ px: 1.5, py: 1, borderBottom: "1px solid #e1e5ec", bgcolor: "#eef1f6" }}>
            <Typography
              sx={{
                fontWeight: 700,
                color: "#5f6878",
                fontSize: "0.85rem",
                textTransform: "uppercase",
                letterSpacing: 0.3,
              }}
            >
              Filters
            </Typography>
          </Box>
          <Box sx={{ p: 1.5 }}>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>Account</InputLabel>
              <Select
                label="Account"
                value={accountFilter}
                onChange={(e) => setAccountFilter(e.target.value)}
              >
                {accounts.map((a) => (
                  <MenuItem key={a} value={a}>{a}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>
      )}

      {/* Token table */}
      <Box
        sx={{
          border: "1px solid #d5d9e0",
          borderRadius: 2,
          overflow: "hidden",
          bgcolor: "#f6f7fa",
        }}
      >
        {isLoading ? (
          <Box sx={{ p: 3, display: "flex", justifyContent: "center" }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <APITokenTable
            tokens={visibleTokens}
            showAccountCol={isAllMode}
            onEdit={(token) => setDrawerTarget({ isNew: false, ...token })}
            onDisable={handleDisable}
            onEnable={handleEnable}
            onDelete={(token) => setPendingDelete(token)}
            onHistory={(token) => setHistoryTarget(token)}
          />
        )}
      </Box>

      {/* Add / Edit Drawer */}
      <APITokenFormDrawer
        open={!!drawerTarget}
        drawerTarget={drawerTarget}
        onClose={() => setDrawerTarget(null)}
        onSaved={() => { setDrawerTarget(null); refetch(); }}
        account={account}
      />

      {/* History Drawer */}
      <APITokenHistoryDrawer
        open={!!historyTarget}
        token={historyTarget}
        onClose={() => setHistoryTarget(null)}
      />

      {/* Delete confirmation dialog */}
      <Dialog open={!!pendingDelete} onClose={() => setPendingDelete(null)}>
        <DialogTitle>Delete Token?</DialogTitle>
        <DialogContent>
          <Typography>
            You are about to permanently delete token{" "}
            <b>{pendingDelete?.name}</b>. This cannot be reversed.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingDelete(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast notifications */}
      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert
          severity={toast.severity}
          variant="filled"
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          sx={{ width: "100%" }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
